import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { style } from './checkbox-filters.style.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { RangeFilterEventDetail, RangeFilterOnFilterEvent } from 'components/discovery/plp/filters/range-filter/range-filter.js'
import { renderAccordionIcons } from 'components/discovery/plp/filters/plp-filters/accordion-icons.js'
import { ActiveRangeFilter, RangeFilter } from 'components/discovery/plp/filters/range-filter/typing/range-filter-types.js'
import {
  APIProperty,
  APIBrand,
  APIPropertyValue,
  FilterChangeDetail
} from 'components/discovery/plp/filters/plp-filters/typing/filters-typing.js'
import { CSECheckbox, CSETextInput } from '@cloudsuite/mosaic-elements'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'
import 'components/discovery/plp/filters/range-filter/range-filter.js'

initializeIconsByRole({
  'csc-checkbox-filters.info': { name: 'circle-info', style: 'regular' },
  'csc-checkbox-filters.search': { name: 'search', style: 'light' }
})

const SEARCH_THRESHOLD = 15

const matchesSearch = (name: string, query: string): boolean => {
  return name.toLowerCase().includes(query.trim().toLowerCase())
}

export class CheckboxChangeEvent extends Event {
  static type = 'checkbox-change' as const

  change: FilterChangeDetail

  constructor(change: FilterChangeDetail) {
    super(CheckboxChangeEvent.type, { bubbles: true, composed: true })
    this.change = change
  }
}

export class CheckboxRangeChangeEvent extends Event {
  static type = 'checkbox-range-change' as const

  change: RangeFilterEventDetail

  constructor(change: RangeFilterEventDetail) {
    super(CheckboxRangeChangeEvent.type, { bubbles: true, composed: true })
    this.change = change
  }
}

export const checkboxFiltersHiddenFields = ['count', 'stock'] as const

@customElement('csc-checkbox-filters')
export class CSCCheckboxFilters extends HiddenFieldsMixin(LitElement, checkboxFiltersHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array, attribute: 'brand-refinements' })
  brandRefinements: APIBrand[] = []

  @property({ type: Array, attribute: 'property-refinements' })
  propertyRefinements: APIProperty[] = []

  @property({ type: Boolean, attribute: 'stock-selected' })
  stockSelected = false

  @property({ type: String, attribute: 'heading-variant' })
  headingVariant = 'heading-4'

  @property({ type: String, attribute: 'accordion-size' })
  accordionSize = 'md'

  @property({ type: Object, attribute: 'range-filter' })
  rangeFilterData: RangeFilter | null = null

  @property({ type: Object, attribute: 'active-range' })
  activeRangeFilter: ActiveRangeFilter | null = null

  @property({ type: Number, attribute: 'brands-display-limit' })
  brandsDisplayLimit = 5

  @state()
  private searchQueries: Record<string, string> = {}

  private emitFilterChange(detail: FilterChangeDetail): void {
    this.dispatchEvent(new CheckboxChangeEvent(detail))
  }

  private handleBrandClick = (brand: APIBrand, event: Event): void => {
    const checkbox = event.currentTarget as CSECheckbox
    brand.selected = checkbox.checked
    this.emitFilterChange({
      type: 'brand',
      id: brand.id,
      selected: checkbox.checked,
      name: brand.name
    })
  }

  private handlePropertyClick = (propertyId: number, value: APIPropertyValue, event: Event): void => {
    const checkbox = event.currentTarget as CSECheckbox
    value.selected = checkbox.checked
    this.emitFilterChange({
      type: 'property',
      propertyId,
      valueId: value.id,
      selected: checkbox.checked,
      name: value.name
    })
  }

  private handleStockClick = (event: Event): void => {
    const checkbox = event.currentTarget as CSECheckbox
    this.stockSelected = checkbox.checked
    this.emitFilterChange({
      type: 'stock',
      selected: checkbox.checked
    })
  }

  private handleRangeChange = (event: RangeFilterOnFilterEvent): void => {
    event.stopPropagation()
    this.dispatchEvent(new CheckboxRangeChangeEvent(event.detail))
  }

  private setSearchQuery(groupKey: string, query: string): void {
    this.searchQueries = { ...this.searchQueries, [groupKey]: query }
  }

  private handleSearchInput = (groupKey: string, event: Event): void => {
    this.setSearchQuery(groupKey, (event.target as CSETextInput).value)
  }

  private clearSearch = (groupKey: string): void => {
    this.setSearchQuery(groupKey, '')

    const textInput = this.renderRoot.querySelector<CSETextInput>(`cse-text-input[data-group="${groupKey}"]`)
    void textInput?.updateComplete.then(() => {
      textInput.input.value = ''
    })
  }

  private isRangeProperty(property: APIProperty): boolean {
    return property.type === 'dec' && property.max !== undefined && property.min !== undefined
  }

  private toRangeFilter(property: APIProperty): RangeFilter {
    return {
      id: property.id,
      type: property.type,
      name: property.name,
      description: property.description,
      values: property.values,
      count: property.count ?? 0,
      graph: property.graph ?? false,
      max: property.max ?? '0',
      min: property.min ?? '0',
      uom: {
        name: (property.uom && property.uom.name) ?? '',
        symbol: (property.uom && property.uom.symbol) ?? ''
      }
    }
  }

  private renderAccordion(
    title: string | TemplateResult | ReturnType<typeof t>,
    content: unknown,
    tooltip?: string,
    open = false,
  ) {
    return html`
      <cse-accordion
        variant="panel"
        size=${this.accordionSize}
        ?open=${open}
      >
        ${renderAccordionIcons(this.accordionSize)}
        <div class="title-container" slot="title">
          <cse-text as="h3" variant="${this.headingVariant}" has-icon>
            ${title}
            ${tooltip ? html`
              <cse-tooltip slot="suffix" class="info-icon">
                <cse-icon slot="anchor" icon-role="csc-checkbox-filters.info"></cse-icon>
                ${tooltip}
              </cse-tooltip>
            ` : nothing}
          </cse-text>
        </div>
        <div slot="content">${content}</div>
      </cse-accordion>
    `
  }

  private renderGroupSearch(groupKey: string, placeholder: TranslateResult, query: string, matchCount: number): TemplateResult {
    return html`
      <div class="filter-search">
        <cse-text-input
          type="text"
          valid="none"
          clearable
          data-group="${groupKey}"
          .value=${query}
          placeholder="${placeholder}"
          @input=${(event: Event) => this.handleSearchInput(groupKey, event)}
          @cse-text-input-cleared=${() => this.clearSearch(groupKey)}
        >
          <cse-icon slot="prefix" icon-role="csc-checkbox-filters.search"></cse-icon>
        </cse-text-input>

        ${query ? html`
          <div class="filter-search-feedback">
            <cse-text variant="body-s">
              ${matchCount
                ? t('results_found', {
                  ns: 'csc-checkbox-filters',
                  count: matchCount,
                  default: '{{variable_count}} result found.',
                  default_plural: '{{variable_count}} results found.',
                  variable_count: matchCount
                })
                : t('no_results', { ns: 'csc-checkbox-filters', default: 'No results found.' })}
            </cse-text>
            <cse-link 
              class="clear-search" 
              font-variant="body-s" 
              @click=${() => this.clearSearch(groupKey)}
            >
              ${t('clear_search', { ns: 'csc-checkbox-filters', default: 'Clear search' })}
            </cse-link>
          </div>
        ` : nothing}
      </div>
    `
  }

  private renderCheckboxList<T extends { id: number; name: string; count?: number; selected?: boolean; description?: string }>(
    options: {
      groupKey: string
      placeholder: TranslateResult
      items: T[]
      onClick: (item: T, event: Event) => void
      keyFn: (item: T) => string | number
      renderContent?: ((item: T) => TemplateResult) | undefined
      visibleOnInit?: number
      showToggle?: boolean
    }
  ): TemplateResult {
    const { groupKey, placeholder, items, onClick, keyFn, renderContent, visibleOnInit = 5, showToggle = true } = options

    const isSearchable = items.length >= SEARCH_THRESHOLD
    const query = isSearchable ? (this.searchQueries[groupKey] ?? '') : ''
    const visibleItems = query ? items.filter(item => matchesSearch(item.name, query)) : items

    return html`
      ${isSearchable ? this.renderGroupSearch(groupKey, placeholder, query, visibleItems.length) : nothing}
      ${visibleItems.length ? this.renderExpandableList(visibleItems, onClick, keyFn, renderContent, visibleOnInit, showToggle) : nothing}
    `
  }

  private renderExpandableList<T extends { id: number; name: string; count?: number; selected?: boolean; description?: string }>(
    items: T[],
    onClick: (item: T, event: Event) => void,
    keyFn: (item: T) => string | number,
    renderContent: ((item: T) => TemplateResult) | undefined,
    visibleOnInit: number,
    showToggle: boolean
  ): TemplateResult {
    return html`
      <cse-expandable variant="list" visible-on-init="${visibleOnInit}">
        ${showToggle ? html`
          <cse-link slot="toggle-close">${t('show_less', { ns: 'csc-checkbox-filters', default: 'Show less' })}</cse-link>
          <cse-link slot="toggle-open">${t('show_more', { ns: 'csc-checkbox-filters', default: 'Show more' })}</cse-link>
        ` : nothing}
        <cse-list>
          ${repeat(
            items,
            keyFn,
            item => html`
              <cse-list-item>
                <div class="checkbox-item">
                  <cse-checkbox
                    @click=${(e: Event) => onClick(item, e)}
                    style="cursor: pointer"
                    .checked=${item.selected ?? false}
                    data-id="${item.id}"
                  >
                    ${renderContent ? renderContent(item) : item.name}
                  </cse-checkbox>

                  ${item.description ? html`
                    <cse-tooltip class="info-icon">
                      <cse-icon slot="anchor" size="sm" icon-role="csc-checkbox-filters.info"></cse-icon>
                      ${item.description}
                    </cse-tooltip>
                  ` : nothing}
                </div>
                ${!this.isHidden('count') && item.count ? html`<cse-badge variant="default" size="small">${item.count}</cse-badge>` : nothing}
              </cse-list-item>
            `
          )}
        </cse-list>
      </cse-expandable>
    `
  }

  private renderColorSwatchContent = (value: APIPropertyValue): TemplateResult => {
    return html`
      <div class="swatch-content">
        <cse-swatch
          variant="color"
          color="${value.css_color}"
          ?selected=${value.selected}
          size="small"
        ></cse-swatch>
        <span class="swatch-text">${value.name}</span>
      </div>
    `
  }

  private renderBrands(brands: APIBrand[], open: boolean): TemplateResult {
    const visibleOnInit = Math.min(this.brandsDisplayLimit, brands.length)

    return this.renderAccordion(
      t('brands', { ns: 'csc-checkbox-filters', default: 'Brands' }),
      this.renderCheckboxList({
        groupKey: 'brands',
        placeholder: t('search_brands_placeholder', { ns: 'csc-checkbox-filters', default: 'Search brands' }),
        items: brands,
        onClick: this.handleBrandClick,
        keyFn: brand => `brand-${brand.id}`,
        visibleOnInit,
        showToggle: Number.isFinite(this.brandsDisplayLimit)
      }),
      undefined,
      open,
    )
  }

  private renderProperty(property: APIProperty, open: boolean): TemplateResult | typeof nothing {
    if (!property.values?.length) return nothing

    const isColorMode = property.display_mode === 'color'

    const content = this.renderCheckboxList({
      groupKey: `property-${property.id}`,
      placeholder: t('search_placeholder', {
        ns: 'csc-checkbox-filters',
        default: 'Search {{variable_group}}',
        variable_group: property.name.toLowerCase()
      }),
      items: property.values,
      onClick: (value, event) => this.handlePropertyClick(property.id, value, event),
      keyFn: value => `prop-${property.id}-${value.id}`,
      renderContent: isColorMode ? this.renderColorSwatchContent : undefined
    })

    return this.renderAccordion(
      property.name,
      content,
      property.description || undefined,
      open,
    )
  }

  private renderStockFilter(): TemplateResult | typeof nothing {
    if (this.isHidden('stock')) return nothing

    const content = html`
      <cse-list>
        <cse-list-item>
          <cse-checkbox
            @click=${this.handleStockClick}
            style="cursor: pointer"
            .checked=${this.stockSelected}
            class="stock-checkbox"
            data-id="-1"
          >
            ${t('in_stock', { ns: 'csc-checkbox-filters', default: 'In Stock' })}
          </cse-checkbox>
        </cse-list-item>
      </cse-list>
    `

    return this.renderAccordion(t('stock', { ns: 'csc-checkbox-filters', default: 'Stock' }), content)
  }

  private renderRangeFilter(rangeProperty?: APIProperty): TemplateResult | null {
    const rangeData = this.rangeFilterData ?? (rangeProperty ? this.toRangeFilter(rangeProperty) : null)
    if (!rangeData) return null

    const activeRange = this.activeRangeFilter ?? { min: parseFloat(rangeData.min), max: parseFloat(rangeData.max) }

    return html`
      <csc-range-filter
        .range=${rangeData}
        .selectedRange=${activeRange}
        .headingVariant=${this.headingVariant}
        .accordionSize=${this.accordionSize}
        @filter-on-filter=${this.handleRangeChange}
      ></csc-range-filter>
    `
  }

  override render() {
    const brands = Array.isArray(this.brandRefinements) ? this.brandRefinements : []
    const allProperties = Array.isArray(this.propertyRefinements) ? this.propertyRefinements : []

    const rangeProperty = allProperties.find(prop => this.isRangeProperty(prop))
    const properties = allProperties.filter(prop => !this.isRangeProperty(prop))

    const hasBrands = brands.length > 0
    const hasProperties = properties.length > 0
    const hasRange = !!this.rangeFilterData || !!rangeProperty
    const hasStock = !this.isHidden('stock')

    if (!hasBrands && !hasProperties && !hasRange && !hasStock) {
      return nothing
    }

    let openSlots = 2
    const brandOpen = hasBrands && openSlots-- > 0
    const propertyOpenStates = properties.map(() => openSlots-- > 0)

    return html`
      <div class="container">
        ${hasBrands ? this.renderBrands(brands, brandOpen) : nothing}
        ${repeat(
          properties,
          prop => `property-${prop.id}`,
          (prop, index) => this.renderProperty(prop, propertyOpenStates[index] ?? false)
        )}
        ${this.renderRangeFilter(rangeProperty)}
        ${this.renderStockFilter()}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-checkbox-filters': CSCCheckboxFilters
  }

  interface GlobalEventHandlersEventMap {
    [CheckboxChangeEvent.type]: CheckboxChangeEvent
    [CheckboxRangeChangeEvent.type]: CheckboxRangeChangeEvent
  }
}
